import React, { useCallback, useRef, useState } from "react";
import {
  Descriptions,
  Image,
  List,
  Space,
  Tooltip,
  Typography,
} from "antd";
import { EyeOutlined, DownloadOutlined } from "@ant-design/icons";
import { Button, Modal, PageHeader } from "../../components/Common";
import PaginatedTable from "../../components/PaginatedTable";
import { useMutations } from "../../hooks/useApiMutation";
import apiService from "../../Utilities/APIService";

const FeedbacksList = () => {
  const tableRef = useRef(null);
  const { mutate, loading } = useMutations();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [attachmentContent, setAttachmentContent] = useState({});

  const fetchData = useCallback(
    async (page, pageSize, search) => {
      try {
        const { data } = await mutate("fetchFeedbacks", {
          method: "get",
          endpoint: "/feedbacks",
          params: { page, pageSize, search },
        });
        return {
          data: data?.data?.items || [],
          total: data?.data?.totalCount || 0,
        };
      } catch {
        return { data: [], total: 0 };
      }
    },
    [mutate]
  );

  const loadDetails = async (id) => {
    const { data } = await mutate("fetchFeedbackDetail", {
      method: "get",
      endpoint: `/feedbacks/${id}`,
      responseConfig: { showFailed: false },
    });
    if (data?.data) {
      const feedback = data.data;
      setSelectedFeedback(feedback);
      setAttachmentContent({});
      if (Array.isArray(feedback.attachments) && feedback.attachments.length) {
        const contentResults = await Promise.all(
          feedback.attachments.map((item) =>
            mutate("fetchFeedbackAttachmentContent", {
              method: "get",
              endpoint: `/feedbacks/attachments/${item.id}/content`,
              responseConfig: { showFailed: false },
            })
          )
        );
        const nextContent = {};
        contentResults.forEach((response, index) => {
          const attachment = feedback.attachments[index];
          if (response?.data?.data) {
            nextContent[attachment.id] = response.data.data;
          }
        });
        setAttachmentContent(nextContent);
      }
      setDetailsOpen(true);
    }
  };

  const handleDownload = async (attachment) => {
    if (!attachment?.id) return;
    setDownloadingId(attachment.id);
    try {
      const response = await apiService.get(
        `/feedbacks/attachments/${attachment.id}`,
        {},
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], {
        type: attachment.contentType || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download =
        attachment.fileName || `feedback-attachment-${attachment.id}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloadingId(null);
    }
  };

  const columns = [
    { title: "AI Type", dataIndex: "aiType", key: "aiType" },
    {
      title: "Explanation",
      dataIndex: "explanation",
      key: "explanation",
      render: (value) => (
        <Typography.Paragraph ellipsis={{ rows: 2 }}>
          {value}
        </Typography.Paragraph>
      ),
    },
    { title: "API Key", dataIndex: "apiKey", key: "apiKey" },
    { title: "User Name", dataIndex: "userName", key: "userName" },
    { title: "User Email", dataIndex: "userEmail", key: "userEmail" },
    {
      title: "Attachments",
      dataIndex: "attachmentCount",
      key: "attachmentCount",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (d) => (d ? new Date(d).toLocaleString() : ""),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Tooltip title="View"><Button variant="ghost" icon={<EyeOutlined />} onClick={() => loadDetails(record.id)} /></Tooltip>
      ),
    },
  ];

  const attachments = selectedFeedback?.attachments || [];

  return (
    <>
      <PageHeader
        title="Feedbacks"
        subtitle="End-user feedback submitted from client applications."
      />
      <PaginatedTable
        ref={tableRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
      />

      {/* View-only modal — no footer confirm/cancel needed */}
      <Modal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        title="Feedback Details"
        size="lg"
        footer={null}
      >
        {selectedFeedback ? (
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="AI Type">
                {selectedFeedback.aiType}
              </Descriptions.Item>
              <Descriptions.Item label="API Key">
                {selectedFeedback.apiKey}
              </Descriptions.Item>
              <Descriptions.Item label="User Name">
                {selectedFeedback.userName || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="User Email">
                {selectedFeedback.userEmail || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Explanation">
                <Typography.Paragraph>
                  {selectedFeedback.explanation}
                </Typography.Paragraph>
              </Descriptions.Item>
              <Descriptions.Item label="Additional Info">
                <Typography.Paragraph>
                  {selectedFeedback.additionalInfo || "-"}
                </Typography.Paragraph>
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {selectedFeedback.createdAt
                  ? new Date(selectedFeedback.createdAt).toLocaleString()
                  : ""}
              </Descriptions.Item>
            </Descriptions>

            <div>
              <Typography.Title level={5} style={{ marginBottom: 12 }}>
                Attachments
              </Typography.Title>
              {attachments.length ? (
                <List
                  dataSource={attachments}
                  renderItem={(item) => {
                    const preview = attachmentContent[item.id];
                    const previewUrl =
                      preview?.contentType?.startsWith("image/")
                        ? `data:${preview.contentType};base64,${preview.dataBase64}`
                        : null;

                    return (
                      <List.Item
                        actions={[
                          <Tooltip title="Download"><Button
                            key={`download-${item.id}`}
                            variant="ghost"
                            icon={<DownloadOutlined />}
                            loading={downloadingId === item.id}
                            onClick={() => handleDownload(item)}
                          /></Tooltip>,
                        ]}
                      >
                        <List.Item.Meta
                          title={item.fileName}
                          description={`${item.contentType || "File"} · ${
                            item.fileSize
                          } bytes`}
                        />
                        {previewUrl ? (
                          <Image
                            width={120}
                            src={previewUrl}
                            alt={item.fileName}
                          />
                        ) : (
                          <Typography.Text type="secondary">
                            {preview
                              ? "Preview not available for this file type."
                              : loading.fetchFeedbackAttachmentContent
                              ? "Loading preview..."
                              : "Preview not available."}
                          </Typography.Text>
                        )}
                      </List.Item>
                    );
                  }}
                />
              ) : (
                <Typography.Text type="secondary">
                  No attachments were provided.
                </Typography.Text>
              )}
            </div>
          </Space>
        ) : (
          <Typography.Text type="secondary">
            Select a feedback to view details.
          </Typography.Text>
        )}
      </Modal>
    </>
  );
};

export default FeedbacksList;
